import React from 'react';
import styles from './index.module.scss';
import classNames from 'classnames';
import updateImageListSrc from "@utils/update-image-list-src";
import { BASE_IMG_URL } from '@images/imageUrls';
import { getProtectedEventHandlers, isContentProtectionEnabled, protectedMediaStyle } from '@utils/contentProtection';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
// 导入 Swiper 样式
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
// 比例计算工具函数
const calculateAspectRatio = (width, height) => {
  return width / height;
};
// 为你的图片样式明确定义类型
interface ImageStyles {
  width: string;
  height: string;
  objectFit: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

// 安全比例判断函数
const getSafeAspectRatio = (naturalRatio) => {
  const MIN_PORTRAIT = 3/4;   // 0.75
  const MAX_LANDSCAPE = 4/3;  // 1.333
  
  // 如果比例在安全范围内，使用原比例
  if (naturalRatio >= MIN_PORTRAIT && naturalRatio <= MAX_LANDSCAPE) {
    return naturalRatio;
  }
  
  // 否则使用强制安全比例
  return naturalRatio < 1 ? MIN_PORTRAIT : MAX_LANDSCAPE;
};
interface PCThreadDetailImgListProps {
  imageList: any
  className?: any
  allowDownload?: boolean
}
interface PCThreadDetailImgListState {
  containerAspectRatio: any
  containerType: any
  activeIndex: any
  isInitialized: any
}
class PCThreadDetailImgList extends React.Component<PCThreadDetailImgListProps, PCThreadDetailImgListState> {
  constructor(props) {
    super(props);
    this.state = {
      containerAspectRatio: null,
      containerType: 'landscape', // 'portrait' 或 'landscape'
      activeIndex: 0,
      isInitialized: false
    };
  }

  componentDidMount() {
    this.initializeCarousel();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.imageList !== this.props.imageList) {
      this.initializeCarousel();
    }
  }

  // 初始化轮播图比例
  initializeCarousel = () => {
    const { imageList } = this.props;
    
    if (!imageList || imageList.length === 0) {
      this.setState({ 
        containerAspectRatio: 4/3,
        containerType: 'landscape',
        isInitialized: true
      });
      return;
    }

    // 获取第一张图片的宽高信息
    const firstImage = imageList[0];
    if (firstImage.fileWidth && firstImage.fileHeight) {
      const naturalRatio = calculateAspectRatio(firstImage.fileWidth, firstImage.fileHeight);
      const safeRatio = getSafeAspectRatio(naturalRatio);
      const containerType = safeRatio < 1 ? 'portrait' : 'landscape';
      
      this.setState({
        containerAspectRatio: safeRatio,
        containerType: containerType,
        isInitialized: true
      });
    } else {
      // 如果没有宽高信息，使用默认比例
      this.setState({
        containerAspectRatio: 4/3,
        containerType: 'landscape',
        isInitialized: true
      });
    }
  };

  // 处理图片加载错误
  handleImageError = (index) => {
    console.warn(`图片 ${index + 1} 加载失败`);
  };

  // 滑动变化回调
  handleSlideChange = (swiper) => {
    this.setState({ activeIndex: swiper.activeIndex });
  };

  // 获取图片显示样式
  getImageStyle = (): React.CSSProperties => {
    const { containerType } = this.state;
  
    if (containerType === 'portrait') {
      return { 
        width: 'auto', 
        height: '100%',
        objectFit: 'contain' as const // 使用 const 断言
      };
    } else {
      return { 
        width: '100%', 
        height: 'auto',
        objectFit: 'contain' as const // 使用 const 断言
      };
    }
  };

  render() {
    const { imageList, className = '', allowDownload } = this.props;
    const { containerAspectRatio, containerType, isInitialized, activeIndex } = this.state;
    const isProtected = isContentProtectionEnabled(allowDownload);
    const protectedHandlers = getProtectedEventHandlers(isProtected);

    if (!imageList || imageList.length === 0) {
      return (
        <div className={`image-carousel empty ${className}`}>
          <div className="carousel-placeholder">暂无图片</div>
        </div>
      );
    }

    if (!isInitialized) {
      return (
        <div className={`image-carousel loading ${className}`}>
          <div className="carousel-loading">加载中...</div>
        </div>
      );
    }

    const containerStyle = {
      aspectRatio: `${containerAspectRatio}`,
      ...(isProtected ? protectedMediaStyle : {})
    };

    return (<div className={styles.imageListWrap}>
      <div className={classNames(styles.imageCarousel, styles.loading, `${className}`,  `${containerType}`)}>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          autoplay={false}
          loop={false}
          onSlideChange={this.handleSlideChange}
          className={classNames(styles.carouselSwiper)}
        >
          {imageList.map((image, index) => (
            <SwiperSlide key={image.id || index}>
              <div 
                className={classNames(styles.slideContainer)}
                style={containerStyle}
                {...protectedHandlers}
              >
                <img
                  src={image.webpUrl || image.url}
                  alt={image.fileName || `轮播图 ${index + 1}`}
                  className={classNames(styles.carouselImage, index === activeIndex ? styles.active : '')}
                  style={{
                    ...this.getImageStyle(),
                    ...(isProtected ? protectedMediaStyle : {})
                  }}
                  onError={() => this.handleImageError(index)}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className={styles.customPagination1}>
            <div className={`swiper-pagination ${styles.customPagination}`}></div>
            <div className={styles.paginationInfo}>
                <span className={styles.currentIndex}>{activeIndex + 1}</span>
                <span className={styles.slash}>/</span>
                <span className={styles.totalCount}>{imageList?.length}</span>
            </div>
        </div>

        <div className={styles.customPagination2}>
            {imageList?.map((img: any, index: any) => (
                <div className={classNames(styles.pageItem, activeIndex == index ? styles.swiperActive : '')}></div>
            ))}
        </div>
    </div>
    );
  }
}

export default PCThreadDetailImgList;